import { Box, Card, LinearProgress, Stack } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import Empty from '../../../components/common/Empty';
import { DetailedSearch } from '../../../components/search-input/DetailedSearchBar';
import {
  DepartmentSelect,
  StatusSelect,
  TypeSelect,
} from '../../../components/select/SelectList';
import { ListDivider } from '../../../components/style/CommonStyle';
import { MapListWrap, MapListItem } from '../../../components/style/ListStyle';
import {
  CountingText,
  ListContentText,
  ListSubContentText,
  ListSubTitleText,
  ListTitleText,
} from '../../../components/style/TextStyle';
import useResponsive from '../../../hooks/useResponsive';
import { selectAccessToken } from '../../../store/authSlice';
import {
  InspectionScoreListLabel,
  InspectionStatusList,
} from '../../../utils/enum';
import { sliceSerialNumber } from '../../../utils/helper';
import { MapListImageInput } from './CommonComponent/ImageInput';
import { InspectionStatusChips } from './StatusChips/InspectionChip';
import { RatingStatusChips } from './StatusChips/RatingChip';

const PAGE_SIZE = 6;

export default function SearchAndListPanel({
  search,
  setSearch,
  type,
  setType,
  status,
  setStatus,
  department,
  setDepartment,
  onFacilityIdChange,
  bound,
}) {
  const params = {
    searchText: search,
    inspectionStatus: status,
    maintenanceDepartmentId: department,
    categoryId: type,
    minLatitude: bound?.minLat,
    maxLatitude: bound?.maxLat,
    minLongitude: bound?.minLng,
    maxLongitude: bound?.maxLng,
  };

  const accessToken = useSelector(selectAccessToken);
  const tanstackResult = useInfiniteQuery<AxiosResponse>({
    queryKey: [params],
    queryFn: async ({ pageParam = 0 }) => {
      const resp = await axios({
        method: 'GET',
        url: '/api/v1/facility',
        params: {
          ...params,
          page: pageParam + 1,
          pageSize: PAGE_SIZE,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return resp.data;
    },
    getNextPageParam: (_lastGroup, groups) => groups.length,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
  const { data, fetchNextPage, isFetching } = tanstackResult;

  const facilities = useMemo(() => {
    const list = data?.pages.flatMap(page => _.get(page, 'result', [])) || [];

    return [...list];
  }, [data]);

  const count = _.get(data, 'pages.0.count', 0);
  const totalFetched = facilities?.length;

  const mdUp = useResponsive('up', 'md');
  const isEmpty = _.isEmpty(facilities);

  const { ref: lastItemRef } = useFetchNextOnIntersection(
    fetchNextPage,
    !isFetching && totalFetched < count,
  );

  return (
    <Card
      sx={{
        minWidth: { sm: '350px', md: '480px' },
        position: 'relative',
        borderRadius: '0px',
        zIndex: 10,
      }}
    >
      <Card sx={{ borderRadius: '0px' }}>
        <Stack direction="column" px={4} spacing={2} pt={6} pb={2}>
          <DetailedSearch
            placeholder="위치,시설명 또는 관리번호로 검색"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setSearch(e.target.value);
                onFacilityIdChange('');
              }
            }}
            onClick={(e, value) => {
              setSearch(value);
              onFacilityIdChange('');
            }}
          />
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <TypeSelect
              type={type}
              onChange={(e: any) => {
                setType(e.target.value);
                onFacilityIdChange('');
              }}
            />
            <StatusSelect
              status={status}
              onChange={e => {
                setStatus(e.target.value);
                onFacilityIdChange('');
              }}
            />
            <DepartmentSelect
              department={department}
              onChange={(e: any) => {
                setDepartment(e.target.value);
                onFacilityIdChange('');
              }}
            />
          </Stack>
          <Box />
          <CountingText>총 {count}개</CountingText>
        </Stack>
      </Card>
      {isEmpty ? (
        <MapListWrap sx={{ p: 3 }}>
          <Empty />
        </MapListWrap>
      ) : (
        <MapListWrap>
          {_.map(facilities, (facility: any, index) => {
            const isRegularType =
              facility?.inspectionStatus ===
              InspectionStatusList.INSPECTION_NOT_REQUIRED;
            const isLastItem = index + 1 === facilities?.length;
            return (
              <React.Fragment key={facility.facilityId}>
                <MapListItem
                  onClick={() => {
                    onFacilityIdChange(facility.facilityId);
                  }}
                >
                  <Stack sx={{ width: '100%' }} spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Stack spacing={0.5}>
                        <InspectionStatusChips
                          status={facility?.inspectionStatus}
                        />
                        <ListTitleText>{facility?.title || '-'}</ListTitleText>
                        {mdUp ? (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            {isRegularType ? (
                              <Box sx={{ w: 1 }} />
                            ) : (
                              <>
                                <RatingStatusChips
                                  status={facility?.inspectionScore}
                                />
                                <ListContentText>
                                  {
                                    InspectionScoreListLabel[
                                      facility?.inspectionScore
                                    ]
                                  }
                                  &nbsp; |&nbsp;
                                  {facility?.inspectionResultTypes ||
                                    '이상 없음'}
                                </ListContentText>
                              </>
                            )}
                          </Stack>
                        ) : (
                          <Stack alignItems="flex-start">
                            {isRegularType ? (
                              <Box sx={{ w: 1 }} />
                            ) : (
                              <>
                                <RatingStatusChips
                                  status={facility?.inspectionScore}
                                />
                                <ListContentText>
                                  {
                                    InspectionScoreListLabel[
                                      facility?.inspectionScore
                                    ]
                                  }
                                  &nbsp; |&nbsp;
                                  {facility?.inspectionResultTypes ||
                                    '이상 없음'}
                                </ListContentText>
                              </>
                            )}
                          </Stack>
                        )}

                        <ListSubContentText>
                          관리번호{' '}
                          {sliceSerialNumber(facility?.serialNumber) || '-'}
                        </ListSubContentText>
                      </Stack>
                      <MapListImageInput imgUrl={facility?.facilityImageUrl} />
                    </Stack>
                    <Stack spacing={1}>
                      <ListSubTitleText>
                        {facility?.addressName || '-'}
                      </ListSubTitleText>
                      <ListSubContentText>
                        {facility?.addressNote || '-'}
                      </ListSubContentText>
                    </Stack>
                  </Stack>
                </MapListItem>
                {isLastItem && <div ref={lastItemRef} />}
                <ListDivider />
              </React.Fragment>
            );
          })}
        </MapListWrap>
      )}
      {isFetching && (
        <LinearProgress
          style={{ position: 'absolute', bottom: 0, width: '100%' }}
        />
      )}
    </Card>
  );
}
function useFetchNextOnIntersection(fetchNextPage, shouldFetchMore) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observerCallback = ([entry]: IntersectionObserverEntry[]) => {
      if (entry.isIntersecting && shouldFetchMore) {
        fetchNextPage();
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '400px',
    });

    const lastItem = ref.current;

    if (lastItem) {
      observer.observe(lastItem);
    }

    return () => {
      if (lastItem) {
        observer.unobserve(lastItem);
      }
    };
  }, [fetchNextPage, shouldFetchMore]);

  return { ref };
}
